import type { Exercise } from "./schemas/exercise.js";
import { exerciseTraits } from "./exerciseTraits.js";
import type { LogSet, LogSetKind } from "./schemas/logSet.js";
import type { Session } from "./schemas/session.js";
import type { Program, TargetItem } from "./schemas/program.js";
import { targetBand } from "./schemas/program.js";
import type { PlannedSet } from "./schemas/plan.js";
import type { TimedSessionResult } from "./progressTypes.js";
import {
  computeSessionResult,
  type SessionResultExerciseLookup,
} from "./sessionResult.js";
import { exerciseToResultLookup } from "./liveSessionResult.js";
import { resolveDefaultWeight, type MaxForProposal } from "./resolveDefaultWeight.js";
import { effortIntentForSet } from "./planStart.js";
import {
  resolveFullCycleSessions,
  type ResolvedSession,
} from "./programResolution.js";
import { prescribedHardSetsByExercise, roundsPerWeekOf } from "./programPrescription.js";
import { sessionTargetMetric } from "./weeklyTargetActual.js";
import { catalogBySlug, hardSetCreditFor } from "./volumeByMuscle.js";
import {
  zoneFor,
  type TargetZone,
  type WeeklyTargetRow,
} from "./weeklyTargets.js";
import { pickAuthoredText } from "./authoredText.js";
import type { Locale } from "./locale.js";

// AP-10 — the PROJECTION behind the target editor's day×week matrix. AP-7's
// `programWeeklyPrescription` answers "how much does the program prescribe per
// week, on average" (one number per muscle); this keeps the (week, day)
// breakdown so the athlete sees HOW that total distributes across the training
// days and how it ramps across the cycle — the loop that tells them whether to
// touch reps or percentages.
//
// Two metrics, two routes — because a hard set and a tonne are different KINDS
// of number, and reusing the wrong machine for either would be a subtle bug:
//
//   • HARD-SET is a set COUNT, independent of weight. It routes through the
//     SAME per-session primitive AP-7 uses (prescribedHardSetsByExercise +
//     resolveExerciseMuscleCredits, the #538 credit), NOT the session
//     scorer, which gates a set on volume>0 and would drop every RIR/RPE-
//     prescribed working set (no weight ⇒ 0 volume ⇒ uncounted).
//   • TONNAGE is volume, weight×reps with laterality. It routes through the ONE
//     volume definition (`computeSessionResult` on a synthesised prescription
//     session, then `sessionTargetMetric` — the identical per-session router the
//     meter's actual uses), so the projected tonnage and the logged actual are
//     the same kind of number and compare like-for-like.
//
// Honest tonnage limits (hard-set is unaffected): a percent-of-MAX set with no
// known MAX, and an RIR/RPE set the chain cannot answer, carry no prescribed
// weight, so they resolve to 0 kg (resolveDefaultWeight's "blank" - never a
// fake target) and under-report tonnage until a weight basis exists. Bodyweight movements also
// under-report their bw component (the composer carries no bodyweight). Both
// mirror what the actual would show for the same missing data.

// Timestamps are irrelevant to the volume aggregate this reads (metrics route
// off SessionResults directly, never a trailing-window filter), so a fixed epoch
// keeps the synthesis pure and deterministic.
const STAMP = "1970-01-01T00:00:00.000Z";
const SYNTH_SESSION: Session = {
  id: "prescription",
  userSub: "",
  startedAt: STAMP,
  status: "saved",
  createdAt: STAMP,
  updatedAt: STAMP,
};

// A prescribed set → a LogSet for the TONNAGE route only. Weight resolves the
// percent-of-MAX / absolute intent to kg (0 when no basis); reps = the
// prescribed floor. RIR is left off the LogSet, the tonnage route never reads
// it. The weight goes through the logger's own chain (#121): the MAX with its
// source and date, and the effort intent, so an RIR/RPE set resolves to the
// load the logger would prefill instead of counting 0 kg. `nowMs` is injected
// to keep this pure.
const prescribedSetToLogSet = (
  set: PlannedSet,
  exercise: Exercise | undefined,
  max: MaxForProposal | undefined,
  key: string,
  nowMs: number,
): LogSet => {
  const isBodyweight = exerciseTraits(exercise).isBodyweight;
  const { kg } = resolveDefaultWeight(set.targetWeight, {
    userMax: max?.valueKg,
    ...(max?.source !== undefined ? { userMaxSource: max.source } : {}),
    ...(max?.setAt !== undefined ? { userMaxSetAt: max.setAt } : {}),
    lastLogged: undefined,
    effort: effortIntentForSet(set, isBodyweight),
    nowMs,
  });
  const kind: LogSetKind = isBodyweight ? "bodyweight" : "weighted";
  return {
    id: key,
    userSub: "",
    exerciseSlug: exercise?.slug ?? key,
    kind,
    performedAt: STAMP,
    completedAt: STAMP,
    createdAt: STAMP,
    updatedAt: STAMP,
    reps: set.targetReps,
    weight: kg,
  };
};

// One resolved prescription day → a scored TimedSessionResult, so its tonnage
// routes through sessionTargetMetric identically to a logged session.
const prescribedTonnageResult = (
  session: ResolvedSession,
  bySlug: ReadonlyMap<string, Exercise>,
  lookup: ReadonlyMap<string, SessionResultExerciseLookup>,
  maxes: ReadonlyMap<string, MaxForProposal> | null,
  nowMs: number,
): TimedSessionResult => {
  const sets: LogSet[] = [];
  session.entries.forEach((entry, ei) => {
    const ex = bySlug.get(entry.exerciseId);
    const userMax = maxes?.get(entry.exerciseId);
    entry.sets.forEach((ps, si) => {
      sets.push(prescribedSetToLogSet(ps, ex, userMax, `p-${ei}-${si}`, nowMs));
    });
  });
  const result = computeSessionResult(
    {
      session: SYNTH_SESSION,
      setsInSession: sets,
      exerciseCatalog: lookup,
      priorHistory: new Map(),
      profileBodyweightKg: 0,
    },
    STAMP,
  );
  return { result, startedAtMs: 0 };
};

// This day's hard-set contribution to one target, from the direct AP-7 count.
const hardSetsForTarget = (
  hardByExercise: Record<string, number>,
  bySlug: ReadonlyMap<string, Exercise>,
  item: TargetItem,
): number => {
  if (item.axis === "exercise") return hardByExercise[item.key] ?? 0;
  if (item.axis === "muscle") {
    let n = 0;
    for (const [slug, hardSets] of Object.entries(hardByExercise)) {
      const ex = bySlug.get(slug);
      if (!ex) continue;
      n += hardSets * hardSetCreditFor(ex, item.key);
    }
    return n;
  }
  return 0;
};

export type ProjectedTarget = {
  item: TargetItem;
  // [weekIdx][sessionIdx] — the metric this target receives on that day.
  grid: number[][];
  // Per cycle-week total: the round's days summed, converted to a week by
  // roundsPerWeekOf (#497), so it differs from the grid row's sum when a round
  // is not a week. The ramp is this array.
  weekTotals: number[];
  // Each week's total scored against the target band by the meter's own zoneFor,
  // so the caller reinforces zone with glyph + word, never colour alone.
  weekZones: TargetZone[];
};

export type ProgramTargetProjection = {
  weekCount: number;
  // Day-column labels = the program's session templates (D1…Dn).
  sessionNames: string[];
  targets: ProjectedTarget[];
};

// The projection for a program measured against a set of weekly targets. Only
// muscle/exercise × hard-sets/tonnage are scoreable (the editor writes no
// others); `reps` and the legacy `pattern` axis are dropped, exactly as
// computeWeeklyTargets filters them. Returns null when there is nothing to
// project.
//
// ME-3: `targetItems` are the ATHLETE's own targets (the AthleteTargets doc),
// not the program's — the target moved off the program. The program supplies
// only the prescribed structure each week×day cell is scored against; the rows
// (and their bands) come from the athlete's goals.
export const programTargetProjection = (
  program: Program,
  catalog: ReadonlyArray<Exercise>,
  maxes: ReadonlyMap<string, MaxForProposal> | null,
  targetItems: ReadonlyArray<TargetItem>,
  // Reader's language (PE-21): the program's authored text is resolved
  // reader → author → any available. Omitted → the chain starts at the
  // author, never at English.
  locale?: Locale,
  // The clock for the MAX age gate, injected so the projection stays pure.
  nowMs: number = Date.now(),
): ProgramTargetProjection | null => {
  const scoreable = targetItems.filter(
    (i) =>
      (i.axis === "muscle" || i.axis === "exercise") &&
      (i.metric === "hard-sets" || i.metric === "tonnage"),
  );
  if (scoreable.length === 0) return null;

  const weekCount = program.cycleWeeks;
  const sessionCount = program.sessions.length;
  // ⛔ The old `s.name || \`D${i+1}\`` could not survive PE-21: an object
  // is always truthy, so the placeholder would never fire and the object
  // itself would reach the matrix's template literals.
  const sessionNames = program.sessions.map(
    (s, i) =>
      pickAuthoredText(s.name, locale, program.authorLocale) || `D${i + 1}`,
  );
  const needsTonnage = scoreable.some((i) => i.metric === "tonnage");
  const roundsPerWeek = roundsPerWeekOf(program);

  const bySlug = catalogBySlug(catalog);
  const lookup = new Map<string, SessionResultExerciseLookup>();
  if (needsTonnage) {
    for (const ex of catalog) lookup.set(ex.slug, exerciseToResultLookup(ex));
  }

  // Score each (week, day) once, reused across every target. hard-set counts are
  // cheap and always built; tonnage results only when a tonnage target exists.
  const hardByCell: Record<string, number>[][] = Array.from(
    { length: weekCount },
    () => Array.from({ length: sessionCount }, () => ({})),
  );
  const tonnageByCell: (TimedSessionResult | null)[][] = Array.from(
    { length: weekCount },
    () => Array.from({ length: sessionCount }, () => null),
  );
  for (const rs of resolveFullCycleSessions(program, locale)) {
    if (rs.weekIdx >= weekCount || rs.sessionIdx >= sessionCount) continue;
    hardByCell[rs.weekIdx]![rs.sessionIdx] = prescribedHardSetsByExercise(rs);
    if (needsTonnage) {
      tonnageByCell[rs.weekIdx]![rs.sessionIdx] = prescribedTonnageResult(
        rs,
        bySlug,
        lookup,
        maxes,
        nowMs,
      );
    }
  }

  const targets: ProjectedTarget[] = scoreable.map((item) => {
    const [loMult, hiMult] = targetBand(item);
    const bandLow = item.value * loMult;
    const bandHigh = item.value * hiMult;
    const grid: number[][] = [];
    const weekTotals: number[] = [];
    const weekZones: TargetZone[] = [];
    for (let w = 0; w < weekCount; w += 1) {
      const row: number[] = [];
      let total = 0;
      for (let d = 0; d < sessionCount; d += 1) {
        let v = 0;
        if (item.metric === "hard-sets") {
          v = hardSetsForTarget(hardByCell[w]![d]!, bySlug, item);
        } else {
          const r = tonnageByCell[w]![d];
          v = r ? sessionTargetMetric(r, bySlug, item) : 0;
        }
        row.push(v);
        total += v;
      }
      const weekTotal = total * roundsPerWeek;
      grid.push(row);
      weekTotals.push(weekTotal);
      weekZones.push(zoneFor(weekTotal, bandLow, bandHigh));
    }
    return { item, grid, weekTotals, weekZones };
  });

  return { weekCount, sessionNames, targets };
};

// PE-46 — the projection read as the METER's own rows, for ONE cycle week.
//
// The program editor shows the SAME band the logger shows inside its card
// (FocusPanelTargetStrip), because the editor IS the logger: one component in
// one place, never a gemello elsewhere. What differs is the SOURCE of the
// number — the logger folds the sets the athlete has just performed, this folds
// what the program ASKS for the week on screen. A prediction, not a reading.
//
// ⭐ The semantic choice, stated so a later reader doesn't take it for an
// oversight: the projected week total lands in `actual`, and `prescribed` stays
// null. `prescribed` is the meter's SECOND reference — the dashed "the plan
// asks X" mark drawn beside a live actual — and here there is no live actual to
// draw it beside: the prescription is the whole number. Putting it in
// `prescribed` instead would render an empty bar with a floating dash.
//
// ⛔ Nothing is re-scored: `weekZones` was already produced by the meter's own
// `zoneFor` inside programTargetProjection, and the band edges come from the
// same `targetBand` this file already reads. A second scoring path here is
// exactly the divergence the shared helpers exist to prevent.
export const prescriptionTargetRows = (
  projection: ProgramTargetProjection,
  weekIdx: number,
): WeeklyTargetRow[] => {
  // Out of range → no rows, never a row reading 0. The cursor and the
  // projection are derived from the same draft, but they re-render
  // independently, and a fabricated "0 / 12, under" would be a lie for the one
  // frame they disagree.
  if (weekIdx < 0 || weekIdx >= projection.weekCount) return [];
  return projection.targets.map(({ item, weekTotals, weekZones }) => {
    const [loMult, hiMult] = targetBand(item);
    return {
      key: item.key,
      axis: item.axis,
      metric: item.metric,
      actual: weekTotals[weekIdx] ?? 0,
      target: item.value,
      bandLow: item.value * loMult,
      bandHigh: item.value * hiMult,
      zone: weekZones[weekIdx] ?? null,
    };
  });
};
